import { node, number, object } from "prop-types";
import React from "react";

import { AnimatePresence, motion } from "framer-motion";

const defaultInitial = {
  opacity: 0,
  y: 30,
};

const defaultAnimate = {
  opacity: 1,
  y: 0,
  transition: {
    duration: 0.3,
    ease: "easeOut",
  },
};

/**
 * @description StaggerIn animates children in a staggerd fashion. This is different from FnrStaggeredEntry in that the children in StaggerIn will start by filling all their space. So the parent does not grow as the children animate in.
 *
 * @param {object} props
 * @param {ReactNode} props.children
 * @param {number} props.initialDelay - delay before the first child animation in seconds
 * @param {number} props.stepDelay - delay between each child animation in seconds
 * @param {number} props.stepDuration - duration of the animation in seconds
 * @param {object} props.initial - replaces the default animates with a framer motion 'initial' animation object
 * @param {object} props.animate - replaces the default animations with framer motion 'animate' animation object
 *
 * @example
 * <StaggerIn initialDelay={0.3} stepDuration={0.4}>
 *   <p>First</p>
 *   <p>Second</p>
 * </StaggerIn>
 * 
 * <StaggerIn
 *   initial={{ y: 30, opacity: 0 }}
 *   animate={{ y: 0, opacity: 1, transition: { type: 'spring', bounce: .35, damping: 10, duration: .2 } }}
 * >
 *  <p>First</p>
 *  <p>Second</p>
 * </StaggerIn>
 */
export function StaggerIn({
  children,
  initialDelay = 0,
  stepDelay = 0.1,
  stepDuration = 0.3,
  initial,
  animate,
}) {
  const childrenArray = React.Children.toArray(children);

  const initialAnimationState = initial || defaultInitial;
  const animateAnimationState = animate || defaultAnimate;

  return (
    <AnimatePresence>
      {childrenArray.map((child, index) => {
        const delayIn = stepDelay * index + initialDelay;

        return (
          <motion.div
            key={child?.key || index}
            initial={initialAnimationState}
            animate={{
              ...animateAnimationState,
              transition: {
                ...animateAnimationState.transition,
                delay: delayIn,
                duration: stepDuration,
              },
            }}
          >
            {child}
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}

StaggerIn.propTypes = {
  children: node,
  initialDelay: number,
  stepDelay: number,
  stepDuration: number,
  initial: object,
  animate: object,
};